import {
  PropsWithChildren,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';

import { setAuthToken } from '~/shared/lib/api';
import { getParam } from '~/shared/lib/getParam';

import { InitContext } from './InitProvider.context';
import { telegram } from './lib/telegram';

const MAX_RETRIES = 5;
const RETRY_DELAY = 2000;

export const InitProvider = (props: PropsWithChildren) => {
  const { children } = props;
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  const [initState, setInitState] = useState({
    loading: true,
    error: false,
  });

  const [token, setToken] = useState<null | string>(null);

  const init = useCallback(async () => {
    if (retryCountRef.current >= MAX_RETRIES) {
      setInitState((prev) => ({
        ...prev,
        error: true,
        loading: false,
      }));
      return;
    }

    setInitState((prev) => ({
      ...prev,
      loading: true,
    }));

    try {
      // работает только при разработке в браузере, когда запускаем скрипт npm run start (scripts/start.js)
      const isBrowserDevelopment = process.env.IS_BROWSER_DEVELOPMENT;

      // Инициализируем Telegram SDK только если не используем демо пользователя и не разрабатываем в браузере
      if (!isBrowserDevelopment || isBrowserDevelopment === 'false') {
        telegram.init();
      }

      const { param: refParam } = getParam('ref');

      if (!token) {
        const { token: nextToken } = await telegram.createToken(refParam);

        if (nextToken) {
          setAuthToken(nextToken);
          setToken(nextToken);
        }
      }

      setInitState((prev) => ({
        ...prev,
        loading: false,
        error: false,
      }));
      telegram.ready();
      retryCountRef.current = 0;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    } catch (error) {
      console.error(error);

      retryCountRef.current += 1;

      if (retryCountRef.current < MAX_RETRIES) {
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }

        retryTimeoutRef.current = setTimeout(() => {
          init();
        }, RETRY_DELAY);
      } else {
        setInitState((prev) => ({
          ...prev,
          error: true,
          loading: false,
        }));
      }
    } finally {
      if (retryCountRef.current >= MAX_RETRIES) {
        setInitState((prev) => ({
          ...prev,
          loading: false,
        }));
      }
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      init();
    }

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [init, initState.loading, token]);

  return (
    <InitContext.Provider
      value={{
        closeMiniApp: telegram.closeMiniApp,
        error: initState.error,
        loading: initState.loading,
        openLink: telegram.openLink,
        token,
      }}
    >
      {children}
    </InitContext.Provider>
  );
};
